import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined';
import { AttachFileOutlined } from '@mui/icons-material';
import { Stack, Typography, styled } from '@mui/material';
import { FC } from 'react';
import { z } from 'zod';
import { useFormikContext } from 'formik';

import { AttachmentFile } from 'domain/entities/Attachment';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { downloadFile } from 'infrastructure/utils/downloadFile';
import { IconButton, IconContainer } from 'targets/web/components';
import { componentShadows } from 'targets/web/theme/shadows';

export const AttachmentsJobDetailsSchema = z.object({
  attachments: z.array(
    z.object({
      uri: z.string(),
      contentType: z.string(),
      name: z.string(),
    }),
  ),
});

export type AttachmentsJobDetailsData = z.infer<typeof AttachmentsJobDetailsSchema>;

const Container = styled(Stack)(({ theme }) => ({
  flex: '1 0 0',
  alignSelf: 'stretch',
  padding: theme.spacing(6),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  boxShadow: componentShadows.card,
}));

export const NoAttachments: FC = () => {
  const t = useTranslationPrefix('jobs.details_form.attachments');
  return (
    <Container alignItems="center" justifyContent="center" gap={2}>
      <IconContainer padding={2}>
        <AttachFileOutlined sx={{ fontSize: '24px' }} />
      </IconContainer>

      <Typography variant="body1">{t('no_attachments')}</Typography>
    </Container>
  );
};

const AttachmentItem: FC<{
  attachment: AttachmentFile;
  onPreview: () => void;
  onDownload: () => void;
}> = ({ attachment, onPreview, onDownload }) => {
  return (
    <Stack direction="row" justifyContent="space-between" gap={2}>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{
          paddingInline: 2,
          paddingBlock: 1,
          borderRadius: 1,
          borderColor: 'outline',
          borderStyle: 'solid',
          borderWidth: 1,
          width: '100%',
          cursor: 'default',
        }}
      >
        <Stack
          sx={{
            padding: 1.5,
            borderRadius: 1,
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '32px',
            backgroundColor: 'primary.action.selected',
          }}
        >
          <AttachFileOutlined sx={{ color: 'primary.main', fontSize: '20px' }} />
        </Stack>

        <Stack>
          <Typography variant="labelSmall">{attachment.name}</Typography>
          <Typography variant="caption" color="secondary">
            {attachment.contentType}
          </Typography>
        </Stack>
      </Stack>

      <IconButton variant="outlined" color="primary" onClick={onPreview}>
        <RemoveRedEyeOutlinedIcon />
      </IconButton>

      <IconButton variant="outlined" color="secondary" onClick={onDownload}>
        <GetAppOutlinedIcon />
      </IconButton>
    </Stack>
  );
};

const AttachmentsJobDetails: FC = () => {
  const { values } = useFormikContext<AttachmentsJobDetailsData>();
  const t = useTranslationPrefix('jobs.details_form.attachments');

  return (
    <Container gap={4} sx={{ padding: 4 }}>
      <Typography variant="h6">{t('title')}</Typography>
      {values.attachments?.length > 0 ? (
        values.attachments.map((a) => (
          <AttachmentItem
            key={a.uri}
            attachment={a}
            onPreview={() => {
              window.open(a.uri, '_blank');
            }}
            onDownload={() => {
              downloadFile(a.uri, a.name);
            }}
          />
        ))
      ) : (
        <NoAttachments />
      )}
    </Container>
  );
};

export default AttachmentsJobDetails;
